// xadomTableSlice.tsx
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Column, EditingCell, Filter, Row, SelectedColumn, SummaryColumn, TableState } from '../types';
import { calculateAllColumnRatios, calculateMinWidth, calculateScrollValues, findColumnById, findNonGroupColumns } from './helper';
import { applyAutoFit, applyAutoFitAndRecalculateScroll, createInitialTableState, handleAutoFitOnCanvasChange, handleCanvasSizeChange, handleColumnChangeAutoFit, handleColumnOperation, handleColumnWidthChange, handleContentSizeChange, handleTopLevelColumnWidthChange, processColumnsWithValidation } from './logic';

// TablesState 관리: 테이블 ID를 키로 사용하여 각 테이블의 메타데이터를 저장
export interface TablesState {
    tables: {
        [tableId: string]: TableState;
    };
    checkedRows: Row[];
    lastCheckedCell?: {
        tableId: string;
        rowIndex: number;
        columnId: string;
    };
}

// 초기 상태
const initialState: TablesState = {
    tables: {},
    checkedRows: [],
    lastCheckedCell: undefined
};

// Slice 생성
const tablesSlice = createSlice({
    name: 'tables',
    initialState,
    reducers: {
        initializeTable: (
            state,
            action: PayloadAction<{
                tableId: string;
                columns: Column[];
                summaryColumns: SummaryColumn[];
                drawEmptyRows?: boolean;
                fullRowSelect?: boolean;
                rowFilter?: { [key: string]: (row: Row) => Row };
                autoFit?: boolean;
            }>
        ) => {
            const { tableId, columns, summaryColumns, drawEmptyRows, fullRowSelect, rowFilter, autoFit } = action.payload;

            // 컬럼 구조 검증 및 처리
            const processedColumns = processColumnsWithValidation(columns);

            // 요약 컬럼 처리
            const processedSummaryColumns = summaryColumns.reduce((acc, col) => {
                const newCol = { ...col, colspan: col.colspan || 1 };
                acc[col.id] = newCol;
                return acc;
            }, {} as { [summarycolumnId: string]: SummaryColumn });

            // 테이블 초기 상태 생성
            const table = createInitialTableState(
                tableId,
                processedColumns,
                processedSummaryColumns,
                drawEmptyRows,
                fullRowSelect,
                rowFilter,
                autoFit
            );
            
            
            state.tables[tableId] = table;
            
            // 초기화 처리 - canvas 크기 설정과 동일한 로직 적용
            if (table.canvasWidth > 0) {
                // 캔버스 크기가 이미 설정된 경우 즉시 처리
                handleCanvasSizeChange(table, true);
            } else if (autoFit) {
                // autoFit인 경우에만 pending 설정
                table.autoFitPending = true;
            }
            
            // autoFit 여부와 관계없이 스크롤 값은 항상 계산
            if (table.canvasWidth <= 0) {
                calculateScrollValues(table, true);
            }
        },
        setColumns: (
            state,
            action: PayloadAction<{ tableId: string; columns: Column[] }>
        ) => {
            const { tableId, columns } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                // 컬럼 구조 검증 및 처리
                const processedColumns = processColumnsWithValidation(columns);
                table.columns = processedColumns;
                
                // autoFit 처리
                handleColumnChangeAutoFit(table);
            }
        },
        setColumn: (
            state,
            action: PayloadAction<{ tableId: string; column: Column }>
        ) => {
            const { tableId, column } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.columns[column.id] = column;
                handleColumnOperation(table, 'update');
            }
        },
        setColumnWidth: (
            state,
            action: PayloadAction<{ tableId: string; columnId: string; width: number; sourceClass: string }>
        ) => {
            const { tableId, columnId, width } = action.payload;
            const table = state.tables[tableId];
            if (!table) return;

            const targetColumn = findColumnById(table, columnId);
            if (!targetColumn) return;

            const minWidth = calculateMinWidth(targetColumn);
            const adjustedWidth = Math.max(width, minWidth);
            
            // AutoFit 처리
            if (table.autoFit && !targetColumn.parent) {
                // 최상위 컬럼만 처리 (현재 로직에 맞춤)
                handleTopLevelColumnWidthChange(table, targetColumn, adjustedWidth);
            } else {
                // 일반적인 컬럼 너비 조정 로직 (non-autoFit 경우 포함)
                handleColumnWidthChange(table, targetColumn, columnId, adjustedWidth);
            }
            
            // 스크롤 값 재계산
            calculateScrollValues(table, true);
        },
        addColumn: (
            state,
            action: PayloadAction<{ tableId: string; column: Column }>
        ) => {
            const { tableId, column } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                handleColumnOperation(table, 'add', column);
            }
        },
        removeColumn: (
            state,
            action: PayloadAction<{ tableId: string; columnIndex: number }>
        ) => {
            const { tableId, columnIndex } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                handleColumnOperation(table, 'remove', columnIndex);
            }
        },
        setSummaryColumns: (
            state,
            action: PayloadAction<{ tableId: string; summaryColumns: SummaryColumn[] }>
        ) => {
            const { tableId, summaryColumns } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.summaryColumns = summaryColumns.reduce((acc, col) => {
                    acc[col.id] = col;
                    return acc;
                }, {} as { [summarycolumnId: string]: SummaryColumn });
                table.hasSummaryColumn = summaryColumns.length > 0;
            }
        },
        addSummaryColumn: (
            state,
            action: PayloadAction<{ tableId: string; summaryColumn: SummaryColumn }>
        ) => {
            const { tableId, summaryColumn } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.summaryColumns[summaryColumn.id] = summaryColumn;
                table.hasSummaryColumn = true;
            }
        },
        removeSummaryColumn: (
            state,
            action: PayloadAction<{ tableId: string; summarycolumnIndex: number }>
        ) => {
            const { tableId, summarycolumnIndex } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                delete table.summaryColumns[summarycolumnIndex];
                table.hasSummaryColumn = Object.keys(table.summaryColumns).length > 0;
            }
        },
        clearSummaryColumns: (
            state,
            action: PayloadAction<{ tableId: string }>
        ) => {
            const { tableId } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.summaryColumns = {};
                table.hasSummaryColumn = false;
            }
        },
        clearSelectedCell: (
            state,
            action: PayloadAction<{ tableId: string }>
        ) => {
            const { tableId } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.selectedCell = { rowIndex: -1, columnIndex: -1 };
            }
        },
        setRowFilter: (
            state,
            action: PayloadAction<{ tableId: string; filter: { [key: string]: (row: Row) => Row } }>
        ) => {
            const { tableId, filter } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.rowFilter = filter;
            }
        },
        removeRowFilter: (
            state,
            action: PayloadAction<{ tableId: string }>
        ) => {
            const { tableId } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                delete table.rowFilter;
            }
        },
        setSelectedCell: (
            state,
            action: PayloadAction<{ tableId: string; rowIndex: number; columnIndex: number }>
        ) => {
            const { tableId, rowIndex, columnIndex } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.selectedCell = { rowIndex, columnIndex };
            }
        },
        setSelectedColumn: (
            state,
            action: PayloadAction<{ tableId: string; selectedColumn: SelectedColumn }>
        ) => {
            const { tableId, selectedColumn } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.selectedColumn = selectedColumn;
            }
        },
        setHasVerticalScrollbar: (
            state,
            action: PayloadAction<{ tableId: string; hasVerticalScrollbar: boolean }>
        ) => {
            const { tableId, hasVerticalScrollbar } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.hasVerticalScrollbar = hasVerticalScrollbar;
            }
        },
        setHasHorizontalScrollbar: (
            state,
            action: PayloadAction<{ tableId: string; hasHorizontalScrollbar: boolean }>
        ) => {
            const { tableId, hasHorizontalScrollbar } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.hasHorizontalScrollbar = hasHorizontalScrollbar;
            }
        },
        setScrollX: (
            state,
            action: PayloadAction<{ tableId: string; scrollX: number }>
        ) => {
            const { tableId, scrollX } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.scrollX = scrollX;
            }
        },
        setScrollY: (
            state,
            action: PayloadAction<{ tableId: string; scrollY: number }>
        ) => {
            const { tableId, scrollY } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.scrollY = scrollY;
            }
        },
        setScroll: (
            state,
            action: PayloadAction<{ tableId: string; scrollX: number; scrollY: number }>
        ) => {
            const { tableId, scrollX, scrollY } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.scrollX = scrollX;
                table.scrollY = scrollY;
            }
        },
        setMaxScrollX: (
            state,
            action: PayloadAction<{ tableId: string; maxScrollX: number }>
        ) => {
            const { tableId, maxScrollX } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.maxScrollX = maxScrollX;
            }
        },
        setMaxScrollY: (
            state,
            action: PayloadAction<{ tableId: string; maxScrollY: number }>
        ) => {
            const { tableId, maxScrollY } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.maxScrollY = maxScrollY;
            }
        },
        setFilter: (
            state,
            action: PayloadAction<{ tableId: string; columnId: string; filter: Filter }>
        ) => {
            const { tableId, columnId, filter } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                const column = findColumnById(table, columnId);
                if (column) {
                    column.filter = filter;
                }
            }
        },
        setFilters: (
            state,
            action: PayloadAction<{ tableId: string; filters: { [columnId: string]: Filter } }>
        ) => {
            const { tableId, filters } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                Object.entries(filters).forEach(([columnId, filter]) => {
                    table.columns[columnId].filter = filter;
                });
            }
        },
        setEditingCell: (
            state,
            action: PayloadAction<{ tableId: string; editingCell: EditingCell | null }>
        ) => {
            const { tableId, editingCell } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.editingCell = editingCell;
            }
        },
        setCanvasSize: (
            state,
            action: PayloadAction<{ tableId: string; canvasWidth: number; canvasHeight: number }>
        ) => {
            const { tableId, canvasWidth, canvasHeight } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                // 캔버스 크기가 변경된 경우 처리
                const widthChanged = table.canvasWidth !== canvasWidth;
                const heightChanged = table.canvasHeight !== canvasHeight;
                
                if (widthChanged || heightChanged) {
                    table.canvasWidth = canvasWidth;
                    table.canvasHeight = canvasHeight;
                    
                    
                    // 너비 변경 시에만 autoFit 처리 (높이 변경은 스크롤만 재계산)
                    if (widthChanged) {
                        handleCanvasSizeChange(table, true);
                    } else {
                        // 높이만 변경된 경우 스크롤 값만 재계산
                        calculateScrollValues(table, false);
                    }
                }
            }
        },
        setCanvasWidth: (
            state,
            action: PayloadAction<{ tableId: string; canvasWidth: number }>
        ) => {
            const { tableId, canvasWidth } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.canvasWidth = canvasWidth;
                handleCanvasSizeChange(table, false);
            }
        },
        setCanvasHeight: (
            state,
            action: PayloadAction<{ tableId: string; canvasHeight: number }>
        ) => {
            const { tableId, canvasHeight } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.canvasHeight = canvasHeight;
                handleCanvasSizeChange(table, false);
            }
        },
        setContentSize: (
            state,
            action: PayloadAction<{ tableId: string; contentWidth: number; contentHeight: number }>
        ) => {
            const { tableId, contentWidth, contentHeight } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.contentWidth = contentWidth;
                table.contentHeight = contentHeight;
                handleContentSizeChange(table, true);
            }
        },
        setContentWidth: (
            state,
            action: PayloadAction<{ tableId: string; contentWidth: number }>
        ) => {
            const { tableId, contentWidth } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.contentWidth = contentWidth;
                handleContentSizeChange(table, true);
            }
        },
        setContentHeight: (
            state,
            action: PayloadAction<{ tableId: string; contentHeight: number }>
        ) => {
            const { tableId, contentHeight } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.contentHeight = contentHeight;
                handleContentSizeChange(table, false);
            }
        },
        setAutoFit: (
            state,
            action: PayloadAction<{ tableId: string; autoFit: boolean }>
        ) => {
            const { tableId, autoFit } = action.payload;
            
            const table = state.tables[tableId];
            if (table) {
                // 현재 autoFit 상태와 새로운 autoFit 상태가 같으면 아무것도 하지 않음
                if (table.autoFit === autoFit) {
                    return;
                }
                
                table.autoFit = autoFit;

                // autoFit이 활성화되면 적용
                if (autoFit) {
                    calculateAllColumnRatios(table.columns);
                    if (table.canvasWidth > 0) {
                        handleAutoFitOnCanvasChange(table);
                    } else {
                        table.autoFitPending = true;
                    }
                }

                calculateScrollValues(table, true);
            }
        },
        applyAutoFitOnly: (
            state,
            action: PayloadAction<{ tableId: string }>
        ) => {
            const { tableId } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                applyAutoFitAndRecalculateScroll(table);
            }
        },
        applyPendingAutoFit: (
            state,
            action: PayloadAction<{ tableId: string }>
        ) => {
            const { tableId } = action.payload;
            const table = state.tables[tableId];
            if (table && table.autoFit && table.autoFitPending) {
                applyAutoFit(table);
                table.autoFitPending = false;
                calculateScrollValues(table, true);
            }
        },
        clearAllOrderType: (
            state,
            action: PayloadAction<{ tableId: string }>
        ) => {
            const { tableId } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                const nonGroupColumns = findNonGroupColumns(table);
                nonGroupColumns.forEach(col => {
                    delete col.filter?.orderType;
                });
            }
        },
        setDropdownMenu: (
            state,
            action: PayloadAction<{
                tableId: string;
                dropdownMenu: TableState['dropdownMenu'];
            }>
        ) => {
            const { tableId, dropdownMenu } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.dropdownMenu = dropdownMenu;
            }
        },
        addCheckedRowData: (state, action) => {
            state.checkedRows.push(action.payload);
        },
        removeCheckedRowData: (state, action) => {
            state.checkedRows = state.checkedRows.filter(row => row.id !== action.payload);
        },
        setAccountOptions: (
            state,
            action: PayloadAction<{ tableId: string; columnId: string; options: { value: number; label: string }[] }>
        ) => {
            const { tableId, columnId, options } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                const column = findColumnById(table, columnId);
                if (column) {
                    column.options = options;
                }
            }
        },
        setLastCheckedCell: (
            state,
            action: PayloadAction<{
                tableId: string;
                rowIndex: number;
                columnId: string;
            } | undefined>
        ) => {
            state.lastCheckedCell = action.payload;
        },
        setHoveredCell: (
            state,
            action: PayloadAction<{ tableId: string; hoveredCell: { rowIndex: number; columnIndex: number } | null }>
        ) => {
            const { tableId, hoveredCell } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.hoveredCell = hoveredCell;
            }
        },
        openSearchModal: (state, action) => {
            const { tableId } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.isSearchModalOpen = true;
            }
        },
        closeSearchModal: (state, action) => {
            const { tableId } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.isSearchModalOpen = false;
            }
        },
        clearAllTables: (state) => {
            // 모든 테이블 상태 초기화
            state.tables = {};
            state.checkedRows = [];
            state.lastCheckedCell = undefined;
        },
        disposeTable: (state, action: PayloadAction<{ tableId: string }>) => {
            const { tableId } = action.payload;
            // 특정 테이블만 제거
            if (state.tables[tableId]) {
                delete state.tables[tableId];
            }
            // 마지막 체크된 셀이 해당 테이블의 것이라면 제거
            if (state.lastCheckedCell?.tableId === tableId) {
                state.lastCheckedCell = undefined;
            }
        },
    }
});

export const {
    initializeTable,
    setColumns,
    setColumn,
    addColumn,
    removeColumn,
    setSummaryColumns,
    addSummaryColumn,
    removeSummaryColumn,
    clearSummaryColumns,
    setSelectedCell,
    clearSelectedCell,
    setSelectedColumn,
    setHasVerticalScrollbar,
    setHasHorizontalScrollbar,
    setScrollX,
    setScrollY,
    setScroll,
    setMaxScrollX,
    setMaxScrollY,
    setFilter,
    setFilters,
    setRowFilter,
    removeRowFilter,
    setEditingCell,
    setCanvasSize,
    setCanvasWidth,
    setCanvasHeight,
    setContentSize,
    setContentWidth,
    setContentHeight,
    setColumnWidth,
    setAutoFit,
    applyAutoFitOnly,
    applyPendingAutoFit,
    clearAllOrderType,
    setDropdownMenu,
    addCheckedRowData,
    removeCheckedRowData,
    setAccountOptions,
    setLastCheckedCell,
    setHoveredCell,
    openSearchModal,
    closeSearchModal,
    clearAllTables,
    disposeTable,
} = tablesSlice.actions;

export default tablesSlice;